import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IUrlClicks } from '@feathr/blackbox';
import { reportModuleLabels } from '@feathr/blackbox';
import type { ITanStackTableProps } from '@feathr/components';
import { TableStatsCard, TanStackTable } from '@feathr/components';

import { sortRows } from '../../utils/sortRows';
import { getLinkClicksColumns } from './LinkClicksColumns';

import * as styles from './LinkClicksTableCard.css';

type TSort = NonNullable<ITanStackTableProps<IUrlClicks>['sort']>;

const DEFAULT_PAGE_SIZE = 20;

interface IProps {
  clicksByUrl: Record<string, IUrlClicks>;
}

// Resolve a column id to its comparable value for the shared row sort.
function getLinkClicksSortValue(row: IUrlClicks, id: string): number | string {
  return id === 'url' ? row.url : row.clicks;
}

function LinkClicksTableCard({ clicksByUrl }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  // Default to most-clicked first, preserving the pre-TanStack behavior.
  const [sort, setSort] = useState<TSort>([{ id: 'clicks', desc: true }]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Client-side sort + paginate over clicksByUrl; TanStackTable renders the given page.
  const rows: IUrlClicks[] = Object.values(clicksByUrl ?? {});
  const sortedRows = sortRows(rows, sort, getLinkClicksSortValue);

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const clampedPage = Math.min(page, pageCount - 1);
  const pageRows = sortedRows.slice(clampedPage * pageSize, clampedPage * pageSize + pageSize);

  const columns = getLinkClicksColumns(t);

  function handleSortChange(nextSort: TSort): void {
    setSort(nextSort);
    setPage(0);
  }

  function handlePageSizeChange(nextPageSize: number): void {
    setPageSize(nextPageSize);
    setPage(0);
  }

  return (
    <TableStatsCard title={reportModuleLabels.includeLinkClicksTable}>
      <TanStackTable<IUrlClicks>
        className={styles.tableWrapper}
        columns={columns}
        idKey={'url'}
        items={pageRows}
        noDataText={t('No Data')}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
        onSortChange={handleSortChange}
        page={clampedPage}
        pages={pageCount}
        pageSize={pageSize}
        sort={sort}
      />
    </TableStatsCard>
  );
}

export default observer(LinkClicksTableCard);
